import { useState, useEffect } from 'react';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { subscriptionApi, paymentApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Check, Zap, Star, Crown, Loader2, CreditCard, ArrowRight,
  ShieldCheck, Mail, CheckCircle, XCircle, ArrowUpRight
} from 'lucide-react';

const TIER_ORDER = ['starter', 'professional', 'enterprise'];

const TIER_CONFIG = {
  starter: {
    icon: Zap, label: 'Starter', color: '#3b82f6',
    gradient: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700',
    price: 699,
    features: [
      'AI Assistant',
      'Task Management', 'Check-in / Check-out', 'Calendar', 'Audit Logs',
    ],
  },
  professional: {
    icon: Star, label: 'Professional', color: '#8b5cf6',
    gradient: 'from-purple-500 to-purple-600',
    bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700',
    price: 1499,
    features: [
      'Everything in Starter', 'GPS Live Tracking',
      'Smart Task Prioritization', 'Performance Analytics',
    ],
  },
  enterprise: {
    icon: Crown, label: 'Enterprise', color: '#f59e0b',
    gradient: 'from-amber-500 to-amber-600',
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700',
    price: 2499,
    features: [
      'Everything in Professional', 'Customer Intelligence',
      'CRM Integrations', 'Smart Visit Planning', 'Smart Notes', 'Priority Support',
    ],
  },
};

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [tierData, setTierData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isOwner = user?.role === 'owner';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tierRes, subRes] = await Promise.all([
          subscriptionApi.getMyTier(),
          paymentApi.getMySubscription(),
        ]);
        setTierData(tierRes.data);
        setTransactions(subRes.data.transactions || []);
      } catch (e) {
        toast.error('Failed to load subscription data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentTier = tierData?.tier || 'starter';
  const currentIdx = TIER_ORDER.indexOf(currentTier);

  const handleUpgrade = async () => {
    if (!upgradeTarget) return;
    setUpgrading(true);
    try {
      const res = await subscriptionApi.upgrade(upgradeTarget);
      setUpgradeSuccess(res.data);
      setUpgradeTarget(null);
      // Refresh data
      const [tierRes, subRes] = await Promise.all([
        subscriptionApi.getMyTier(),
        paymentApi.getMySubscription(),
      ]);
      setTierData(tierRes.data);
      setTransactions(subRes.data.transactions || []);
      toast.success(`Upgraded to ${res.data.plan_name}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upgrade failed');
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </MainLayout>
    );
  }

  const currentConfig = TIER_CONFIG[currentTier] || TIER_CONFIG.starter;
  const CurrentIcon = currentConfig.icon;

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto space-y-8" data-testid="subscription-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>
            Subscription
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage your plan and billing</p>
        </div>

        {/* Current Plan Card */}
        <Card className={`${currentConfig.border} ${currentConfig.bg}/30 overflow-hidden`} data-testid="current-plan-card">
          <div className={`h-1.5 bg-gradient-to-r ${currentConfig.gradient}`} />
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentConfig.gradient} flex items-center justify-center shadow-lg`}>
                  <CurrentIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Current Plan</p>
                  <p className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                    {currentConfig.label}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {tierData?.is_owner ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">Lifetime Access</Badge>
                    ) : (
                      <Badge className={`${currentConfig.bg} ${currentConfig.text} text-xs`}>Active</Badge>
                    )}
                    {!isAdmin && (
                      <span className="text-xs text-gray-400">Contact your admin to change plans</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                  {tierData?.is_owner ? 'Free' : `\u20B9${currentConfig.price.toLocaleString('en-IN')}`}
                </p>
                {!tierData?.is_owner && <p className="text-xs text-gray-500">per user / month</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upgrade Success Banner */}
        {upgradeSuccess && (
          <Card className="border-green-200 bg-green-50/50" data-testid="upgrade-success-banner">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">
                  Upgraded to {upgradeSuccess.plan_name}!
                </p>
                <p className="text-xs text-green-600">
                  Transaction: {upgradeSuccess.transaction_id} &middot; {upgradeSuccess.currency} {upgradeSuccess.amount?.toLocaleString('en-IN')}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="ml-auto text-green-700" onClick={() => setUpgradeSuccess(null)}>
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TIER_ORDER.map((tierId, idx) => {
            const config = TIER_CONFIG[tierId];
            const Icon = config.icon;
            const isCurrent = tierId === currentTier;
            const isUpgrade = idx > currentIdx;
            const isDowngrade = idx < currentIdx;

            return (
              <Card
                key={tierId}
                className={`relative overflow-hidden transition-all duration-200 hover:shadow-md ${isCurrent ? 'ring-2 ring-offset-1' : ''}`}
                style={isCurrent ? { '--tw-ring-color': config.color } : {}}
                data-testid={`plan-card-${tierId}`}
              >
                <div className={`h-1 bg-gradient-to-r ${config.gradient}`} />
                {isCurrent && (
                  <div className="absolute top-3 right-3">
                    <Badge className={`${config.bg} ${config.text} text-[10px] gap-1`}>
                      <ShieldCheck className="w-3 h-3" /> Current
                    </Badge>
                  </div>
                )}
                <CardContent className="p-5 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>{config.label}</h3>
                  </div>

                  <div className="mb-4">
                    <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                      {"\u20B9"}{config.price.toLocaleString('en-IN')}
                    </span>
                    <span className="text-xs text-gray-500 ml-1">/user/mo</span>
                  </div>

                  <ul className="space-y-2 mb-5">
                    {config.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: config.color }} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* Action button */}
                  {isCurrent ? (
                    <Button variant="outline" className="w-full" disabled data-testid={`btn-current-${tierId}`}>
                      Current Plan
                    </Button>
                  ) : isUpgrade && isAdmin && !isOwner ? (
                    <Button
                      className={`w-full bg-gradient-to-r ${config.gradient} text-white hover:opacity-90 gap-1.5`}
                      onClick={() => setUpgradeTarget(tierId)}
                      data-testid={`btn-upgrade-${tierId}`}
                    >
                      Upgrade <ArrowUpRight className="w-4 h-4" />
                    </Button>
                  ) : isDowngrade && isAdmin ? (
                    <Button
                      variant="outline"
                      className="w-full text-gray-500 gap-1.5"
                      onClick={() => window.location.href = 'mailto:support@aivora-techlabs.com?subject=Downgrade%20Request'}
                      data-testid={`btn-downgrade-${tierId}`}
                    >
                      <Mail className="w-3.5 h-3.5" /> Contact Support
                    </Button>
                  ) : isOwner ? (
                    <Button variant="outline" className="w-full" disabled>
                      Lifetime Access
                    </Button>
                  ) : (
                    <Button variant="outline" className="w-full" disabled>
                      Contact Admin
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Downgrade notice */}
        {isAdmin && !isOwner && currentIdx > 0 && (
          <Card className="border-gray-200 bg-gray-50/50">
            <CardContent className="p-4 flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-700">Need to downgrade?</p>
                <p className="text-xs text-gray-500">
                  Contact us at{' '}
                  <a href="mailto:support@aivora-techlabs.com" className="text-blue-600 hover:underline" data-testid="downgrade-email-link">
                    support@aivora-techlabs.com
                  </a>
                  {' '}and we'll assist you.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction History */}
        {transactions.length > 0 && (
          <Card data-testid="transaction-history">
            <CardContent className="p-5">
              <h3 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>Payment History</h3>
              <div className="space-y-2">
                {transactions.map((txn, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {txn.payment_status === 'paid' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-400" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{txn.plan_name} Plan</p>
                        <p className="text-xs text-gray-500">{new Date(txn.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{"\u20B9"}{txn.amount?.toLocaleString('en-IN')}</p>
                      <Badge className={`text-[10px] ${txn.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {txn.payment_status === 'paid' ? 'Paid' : txn.payment_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upgrade Confirmation Dialog */}
      <Dialog open={!!upgradeTarget} onOpenChange={() => !upgrading && setUpgradeTarget(null)}>
        <DialogContent className="max-w-md" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Confirm Upgrade</DialogTitle>
          </DialogHeader>
          {upgradeTarget && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {(() => { const C = TIER_CONFIG[currentTier]?.icon || Zap; return <C className="w-4 h-4" />; })()}
                  {TIER_CONFIG[currentTier]?.label}
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <div className={`flex items-center gap-2 text-sm font-semibold ${TIER_CONFIG[upgradeTarget]?.text}`}>
                  {(() => { const T = TIER_CONFIG[upgradeTarget]?.icon || Star; return <T className="w-4 h-4" />; })()}
                  {TIER_CONFIG[upgradeTarget]?.label}
                </div>
              </div>

              <div className="border rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Plan</span>
                  <span className="font-medium">{TIER_CONFIG[upgradeTarget]?.label}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Price</span>
                  <span className="font-medium">{"\u20B9"}{TIER_CONFIG[upgradeTarget]?.price.toLocaleString('en-IN')}/user/mo</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Payment</span>
                    <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">Demo Mode</Badge>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Payment gateway integration coming soon. This upgrade will activate immediately.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUpgradeTarget(null)} disabled={upgrading} data-testid="upgrade-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleUpgrade}
              disabled={upgrading}
              className={`gap-1.5 bg-gradient-to-r ${TIER_CONFIG[upgradeTarget]?.gradient} text-white hover:opacity-90`}
              data-testid="upgrade-confirm-btn"
            >
              {upgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {upgrading ? 'Processing...' : 'Confirm Upgrade'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
